import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";

const WHATSAPP_NUMBER = "919740531855"; // +91 97405 31855
const DEFAULT_MESSAGE =
  "Hi Naavi Early Years team! I'd like to know more about admissions for my child.";

const HIDDEN_PREFIXES = ["/admin", "/parent"];

export default function WhatsAppButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // Close popover on route change
  useEffect(() => setOpen(false), [location.pathname]);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (hidden) return null;

  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;

  return (
    <div
      className={`fixed z-[55] right-5 md:right-7 bottom-5 md:bottom-7 transition-all duration-500 ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      data-testid="whatsapp-fab"
    >
      {/* Popover card */}
      {open && (
        <div
          className="absolute bottom-[78px] right-0 w-[320px] bg-[#FDFDF9] rounded-[24px] border border-[#0F1B47]/10 shadow-[0_24px_60px_-20px_rgba(15,27,71,0.25)] overflow-hidden"
          data-testid="whatsapp-popover"
        >
          <div className="bg-[#0F1B47] text-[#FDFDF9] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
              <WaIcon className="w-5 h-5 text-[#FDFDF9]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#C6D947]">
                Admissions
              </div>
              <div className="font-semibold text-sm leading-tight">Naavi Early Years</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-[#FDFDF9]/70 hover:text-[#FDFDF9]"
              data-testid="whatsapp-close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="px-5 py-4">
            <div className="bg-[#F5F5F0] rounded-2xl px-4 py-3 text-sm text-[#0F1B47] leading-relaxed">
              Hi there! 👋 We usually reply within an hour during school hours
              (Mon — Fri, 8:30 AM — 3:30 PM). Tap below to start a chat on WhatsApp.
            </div>
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-[#FDFDF9] rounded-full px-5 py-3 font-semibold text-sm transition-colors"
              data-testid="whatsapp-start-chat"
            >
              <WaIcon className="w-4 h-4 text-[#FDFDF9]" />
              Start chat on WhatsApp
            </a>
            <div className="text-[11px] text-[#718096] text-center mt-3">
              +91 97405 31855
            </div>
          </div>
        </div>
      )}

      {/* FAB button with pulse */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with Naavi admissions on WhatsApp"
        className="relative group"
        data-testid="whatsapp-toggle"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" />
        <span className="relative w-14 h-14 md:w-[58px] md:h-[58px] rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_18px_40px_-10px_rgba(37,211,102,0.55)] transition-transform group-hover:scale-105">
          {open ? (
            <X size={22} className="text-[#FDFDF9]" />
          ) : (
            <WaIcon className="w-7 h-7 text-[#FDFDF9]" />
          )}
        </span>
        {!open && (
          <span className="hidden md:inline-flex absolute right-[72px] top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#0F1B47] text-[#FDFDF9] text-xs font-semibold px-3.5 py-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Chat with us
          </span>
        )}
      </button>
    </div>
  );
}

function WaIcon({ className = "" }) {
  // Simple WhatsApp logo glyph
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.077 4.928A9.5 9.5 0 0 0 12.012 2c-5.246 0-9.5 4.254-9.5 9.5 0 1.66.43 3.272 1.243 4.696L2 22l5.962-1.564a9.46 9.46 0 0 0 4.05.916h.004c5.245 0 9.5-4.254 9.5-9.5a9.5 9.5 0 0 0-2.439-6.924zM12.013 19.667h-.003a7.85 7.85 0 0 1-4.001-1.097l-.287-.171-3.539.928.945-3.45-.187-.298a7.846 7.846 0 0 1-1.205-4.18c0-4.339 3.532-7.87 7.873-7.87a7.83 7.83 0 0 1 5.566 2.305 7.83 7.83 0 0 1 2.305 5.567c-.002 4.34-3.534 7.87-7.467 7.87zm4.323-5.892c-.237-.119-1.402-.692-1.62-.771-.217-.079-.375-.119-.534.119-.158.237-.612.771-.751.929-.139.158-.277.178-.514.059-.237-.119-1.001-.369-1.906-1.176-.704-.628-1.18-1.404-1.319-1.641-.139-.237-.015-.365.104-.484.107-.106.237-.277.355-.415.119-.139.158-.237.237-.395.079-.158.04-.297-.02-.415-.059-.119-.534-1.286-.731-1.762-.193-.462-.388-.4-.534-.408a9.7 9.7 0 0 0-.456-.008c-.158 0-.415.059-.633.297-.217.237-.831.812-.831 1.979s.851 2.295.97 2.453c.119.158 1.675 2.557 4.057 3.585.567.245 1.009.391 1.354.5.569.181 1.087.156 1.496.095.456-.068 1.402-.573 1.6-1.127.197-.553.197-1.027.138-1.127-.059-.099-.217-.158-.454-.277z"/>
    </svg>
  );
}
